import axios from 'axios';
import { WIZARD_DATA_API } from 'Constants';

/** A class the booker can choose on this trip. */
export interface SeatingTabClass {
	id: string;
	name: string;
	abbreviation?: string;
	description?: string;
	order?: number | null;
	/** Whether the booker picks their own seat, rather than the class being unreserved. */
	allowsSeatSelection: boolean;
	/**
	 * What choosing a seat in this class costs, per seat, on top of the fare. Zero means free, which
	 * is every class until an operator sets one.
	 */
	seatSelectionFee?: number | null;
	/** The class passengers start in on this vessel, pre-selected by the seating step. */
	isDefault?: boolean;
	/** Already resolved by the server: the vessel's override, then the class's own, then null. */
	colour?: string | null;
	/** Seats left in this class on the tightest leg. Can be negative when overbooked. */
	/**
	 * Seats left in this fare type, or null where the capacity engine has no figure for the trip.
	 *
	 * <p>Null is "not known", not "none left" — the step shows no count rather than marking the fare
	 * type full, which would stop the booking over a missing number.</p>
	 */
	remaining: number | null;
	/** The class's `Show how many places are left` setting; when false the step prints no count. */
	showCapacity?: boolean;
	/**
	 * The per-head fare in this class for each active passenger type, keyed by that type's key.
	 *
	 * <p>Per type because that is what a class costs: an adult and a child in the same class pay
	 * different amounts, and the step chooses a class for one named passenger at a time — so it shows
	 * that passenger's own fare rather than a sample.</p>
	 */
	fares?: Record<string, number>;
	/**
	 * What this class offers. Carries both the points listed beside the class and the ones reached
	 * through "more info" — one list, because the panel opens from the picker and a second request
	 * for four lines of text would cost more than sending them.
	 */
	attributes?: SeatingTabClassAttribute[];
}

/** One thing a class offers, as the booker reads it. */
export interface SeatingTabClassAttribute {
	label: string;
	/** Material Symbols name drawn beside the label, or blank for a label on its own. */
	iconName?: string | null;
	/** Listed against the class in the picker. */
	showInWizard: boolean;
	/** Listed in the class's "more info" panel. */
	showInMoreInfo: boolean;
}

/** One deck of the vessel, with its own plan. */
export interface SeatingTabDeck {
	id: string;
	name: string;
	order?: number | null;
	/** This deck's gangways, tables and markers, in the deck-feature grammar. */
	deckFeatures?: string | null;
	/** This deck's plan image, when it has one. */
	seatMapImageId?: string | null;
}

/** A seat the booker can pick. */
export interface SeatingTabSeat {
	id: string;
	/** Which deck's plan this seat is drawn on. */
	deckId: string;
	rowLabel: string;
	seatNumber: string;
	label: string;
	order?: number | null;
	/** A seat or a wheelchair bay — tables and aisles never reach the booker. */
	kind?: 'SEAT' | 'WHEELCHAIR_BAY';
	/** The block of the vessel the seat is in; the picker draws one panel per block. */
	section?: string | null;
	/** Where the seat sits across its row, or unset where the vessel has not been told. */
	position?: 'WINDOW' | 'AISLE' | 'INNER' | null;
	facing?: 'FORWARD' | 'BACKWARD' | null;
	side?: 'PORT' | 'STARBOARD' | 'CENTRE' | null;
	/** Empty means unclassed — pickable from any class. */
	passengerClassIds: string[];
	/** Held by another booking on this trip. */
	occupied: boolean;
}

/**
 * Everything the seating step needs for one trip.
 *
 * Classes and seats are independent, so any combination is valid: classes with no seats is
 * unreserved seating with a headcount cap; seats with no classes is one fare and a seat map. Both
 * empty means the ferry has neither and the step is skipped.
 */
export interface SeatingTabData {
	tripId: string;
	/**
	 * The vessel's decks, each with its own layout and plan image. The picker draws one at a time —
	 * seat numbers repeat between decks, so two plans in one grid would draw two 12As.
	 */
	decks: SeatingTabDeck[];
	passengerClasses: SeatingTabClass[];
	seats: SeatingTabSeat[];
}

export async function fetchSeatingTabData(
	tripId: string,
	bookingToEditId?: string | null,
	signal?: AbortSignal,
	/**
	 * The segment being booked. Only the fares need it — but they need it: the server can otherwise
	 * only guess the journey from the trip's own stops, which prices the whole sailing rather than
	 * this leg and comes back empty on a trip whose stop rows are missing.
	 */
	journey?: { startStopId?: string; endStopId?: string },
): Promise<SeatingTabData> {
	const { data } = await axios.get(`${WIZARD_DATA_API}/seating-tab`, {
		params: {
			tripId,
			bookingToEditId: bookingToEditId || undefined,
			startStopId: journey?.startStopId || undefined,
			endStopId: journey?.endStopId || undefined,
		},
		signal,
	});
	return data;
}

/** True when this trip has nothing to choose and the step should be skipped entirely. */
export function seatingIsEmpty(data?: SeatingTabData | null): boolean {
	return !data || (data.passengerClasses.length === 0 && data.seats.length === 0);
}

/**
 * The seats a booker may pick for a given class.
 *
 * An unclassed seat (no classes of its own) is pickable from any class — that is what makes the
 * "seats but no classes" configuration work. Occupied seats are excluded.
 */
export function selectableSeatsForClass(
	seats: SeatingTabSeat[],
	passengerClassId: string | undefined,
	/** Seats already chosen elsewhere in this booking, which must not be offered twice. */
	takenInThisBooking: string[] = [],
): SeatingTabSeat[] {
	return seats.filter(seat => {
		if (seat.occupied || takenInThisBooking.includes(seat.id)) {
			return false;
		}
		if (seat.passengerClassIds.length === 0) {
			return true;
		}
		return passengerClassId != null && seat.passengerClassIds.includes(passengerClassId);
	});
}

/**
 * The decks a class has seats on.
 *
 * <p>Derived from the seats rather than stored: a class's deck is already implied by which seats
 * carry it, and a second link would be a second thing to keep in step. An unclassed seat is pickable
 * from any class, so a deck holding one counts for every class.</p>
 *
 * <p>An empty result means no deck has a seat for this class — unreserved seating, where the class
 * caps a headcount and nobody picks a seat at all.</p>
 */
export function decksForClass(
	decks: SeatingTabDeck[],
	seats: SeatingTabSeat[],
	passengerClassId: string | undefined,
): SeatingTabDeck[] {
	// A deck is offered only where this fare type could actually take a seat on it.
	//
	// "Has seats it is allowed to sit in" is not the same as "has seats": a wheelchair bay is drawn on
	// the plan and sells to nobody, and a deck whose only unclassed entries are bays would otherwise be
	// offered to a fare type that cannot book a single place on it.
	const bookable = seats.filter(seat => !seat.kind || seat.kind === 'SEAT');

	// Two grades of "this fare type can sit here", and the order matters as much as the membership.
	//
	// A deck carrying seats tagged with THIS fare type is where it actually lives. A deck carrying only
	// unclassed seats is merely open to everyone, and almost every deck has a few — so ranking them
	// equally put the main deck first for Premium, whose seats are all upstairs, and the picker opened
	// on a deck the fare type is not sold on.
	const ownDeckIds = new Set(
		bookable
			.filter(seat => passengerClassId != null && seat.passengerClassIds.includes(passengerClassId))
			.map(seat => seat.deckId),
	);

	const openDeckIds = new Set(
		bookable.filter(seat => seat.passengerClassIds.length === 0).map(seat => seat.deckId),
	);

	// Where the fare type has decks of its own, those are the only ones offered. A deck it can reach
	// only through a handful of unclassed seats is not where it is sold, and offering it sends a
	// Premium booker to the main deck to hunt for the two seats there that happen to carry no fare
	// type. Only a fare type with no seats anywhere falls back to the open decks — without that, a
	// vessel that classes nothing would offer no decks at all.
	const offered = ownDeckIds.size > 0
		? decks.filter(deck => ownDeckIds.has(deck.id))
		: decks.filter(deck => openDeckIds.has(deck.id));

	// The vessel's own deck order decides between them.
	return offered;
}
